import { AlertDangerIcon, AlertWarningIcon, Button, Caption, Feedback, TargetIcon, Tooltip } from 'canoe';
import { useFormikContext } from 'formik';
import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import { ValueBox } from '@candu-ai/craft/ui';
import { ContentPlacement } from '@candu-ai/placement/types';
import {
  InlineMessage,
  RetargetButton,
  SelectorInputWrapper,
  SelectorValueInput,
} from 'craft/containers/Toolbox/tabs/Placements/components/ui';
import { checkInvalidSelector } from 'craft/containers/Toolbox/tabs/Placements/utils';
import { setPreviewSelector, useCurrentSnapshot } from 'craft/state';

interface Props {
  handleRetargetClick?: () => void;
  onSave: (value?: string) => void;
  onCancel: VoidFunction;
  isForcedEdit: boolean;
  onDeletePlacementSnapshot: VoidFunction;
}

export const InlineHTMLSelectorForm = ({
  handleRetargetClick,
  onSave,
  onCancel,
  isForcedEdit,
  onDeletePlacementSnapshot,
}: Props) => {
  const dispatch = useDispatch();
  const { snapshot } = useCurrentSnapshot();
  const htmlSelectorRef = useRef<HTMLInputElement>(null);
  const { values } = useFormikContext<ContentPlacement>();

  const [inputValue, setInputValue] = useState(values.selector);

  const { invalidSelector, querySelector } = checkInvalidSelector(inputValue);
  const notVisibleOnPage =
    !invalidSelector && !!values.snapshotId && !querySelector && !!inputValue;

  const disableSave =
    !inputValue ||
    inputValue === values.selector ||
    invalidSelector ||
    (!!values.snapshotId && (invalidSelector || !querySelector));

  const hideCancel = isForcedEdit;

  useEffect(() => {
    dispatch(setPreviewSelector(inputValue));
  }, [inputValue]);

  useEffect(() => {
    // auto-focus
    htmlSelectorRef.current?.select();

    // if we just forced to active and there is a snapshot, go into click to place mode
    if (isForcedEdit && snapshot) {
      handleRetargetClick?.();
    }
  }, []);

  return (
    <Tooltip
      disabled={!notVisibleOnPage}
      content={
        <div>
          <Caption color="White" mb="none">
            Trying to add a custom selector?
          </Caption>
          <Caption color="White" mb="none">
            <Button
              variant="secondary"
              appearance="text"
              inline
              size="xsmall"
              onClick={onDeletePlacementSnapshot}
            >
              Click here
            </Button>{' '}
            to remove the snapshot
          </Caption>
        </div>
      }
      placement="top"
      space="xs"
    >
      <SelectorInputWrapper>
        <ValueBox noBorder>
          <SelectorValueInput
            ref={htmlSelectorRef}
            value={inputValue}
            onChange={(e: ChangeEvent<HTMLInputElement>) => {
              setInputValue(e.target.value || '');
            }}
            hasError={
              invalidSelector ||
              (!inputValue && inputValue !== values.selector) ||
              (!inputValue && !!values.id)
            }
            hasWarning={notVisibleOnPage}
            onKeyDown={(e: KeyboardEvent) => {
              if (!inputValue) {
                return;
              }

              if (e.key === 'Enter' && !disableSave) {
                // if form submit button is not disabled, save change on Enter
                onSave(inputValue);
              } else if (e.key === 'Escape' && !hideCancel) {
                // cancel change on Escape
                onCancel();
              }
            }}
          />
          {handleRetargetClick && (
            <RetargetButton
              appearance="outline"
              title="Retarget"
              onClick={() => {
                handleRetargetClick();
              }}
              variant="neutral"
              size="xsmall"
              data-cy="update-location"
            >
              <TargetIcon />
            </RetargetButton>
          )}
        </ValueBox>
        {(invalidSelector || (!inputValue && inputValue !== values.selector)) && (
          <InlineMessage>
            <Feedback ml="xxs" color="danger" mode="alert" alertIcon={AlertDangerIcon}>
              {invalidSelector ? 'Selector is not valid' : 'Required'}
            </Feedback>
          </InlineMessage>
        )}
        {notVisibleOnPage && (
          <InlineMessage>
            <Feedback ml="xxs" color="warning" mode="alert" alertIcon={AlertWarningIcon}>
              Selector not visible on page
            </Feedback>
          </InlineMessage>
        )}
      </SelectorInputWrapper>
      <div>
        <Button
          variant="secondary"
          fullWidth
          disabled={disableSave}
          onClick={() => onSave(inputValue)}
          mt="xs"
        >
          Update
        </Button>
        {!hideCancel && (
          <Button variant="neutral" appearance="outline" fullWidth onClick={onCancel} mt="xs">
            Cancel
          </Button>
        )}
      </div>
    </Tooltip>
  );
};
